import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import Div from '@hellodarwin/core/lib/components/common/div';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import GrantCard from '@hellodarwin/core/lib/components/grants/card';
import { usePlatform } from '@hellodarwin/core/lib/features/providers/platform-provider';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import FavoriteCandidatesContent from '../../../../../program-candidates-data/components/additional-grants-section/favorite-candidates/favorite-candidates-content';
import { ToolAnalyzeContainer } from '../../styles';
export const AnalyzeGrantCard = ({ tool }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { paths } = usePlatform();
    const accessLevel = usePremiumAccess();
    const grantInfo = useMemo(() => {
        if (!tool.progress || tool.progress.length === 0)
            return null;
        let bestWithTitle;
        let fallbackWithId;
        for (let i = tool.progress.length - 1; i >= 0; i--) {
            const progress = tool.progress[i];
            if (progress.status_key === 'assistant.tool_analyze_grant_card' &&
                !!progress.content) {
                try {
                    const parsed = JSON.parse(progress.content);
                    if ((parsed === null || parsed === void 0 ? void 0 : parsed.grant) || (parsed === null || parsed === void 0 ? void 0 : parsed.title)) {
                        bestWithTitle = progress;
                        break;
                    }
                    if (!fallbackWithId && (parsed === null || parsed === void 0 ? void 0 : parsed.grant_id)) {
                        fallbackWithId = progress;
                    }
                }
                catch (error) {
                    console.warn('[AnalyzeGrantCard] Failed to parse grant card data:', error);
                }
            }
        }
        const chosenProgress = bestWithTitle || fallbackWithId;
        if (!(chosenProgress === null || chosenProgress === void 0 ? void 0 : chosenProgress.content))
            return null;
        try {
            const parsed = JSON.parse(chosenProgress.content);
            if (!(parsed === null || parsed === void 0 ? void 0 : parsed.grant_id))
                return null;
            return parsed;
        }
        catch (error) {
            console.warn('[AnalyzeGrantCard] Failed to decode grant card JSON:', error);
            return null;
        }
    }, [tool]);
    const statisticsData = useMemo(() => {
        if (!tool.progress || tool.progress.length === 0)
            return null;
        let statisticsProgress;
        for (let i = tool.progress.length - 1; i >= 0; i--) {
            const progress = tool.progress[i];
            if (progress.status_key === 'assistant.tool_analyze_grant_card' &&
                !!progress.content) {
                try {
                    const parsed = JSON.parse(progress.content);
                    if (parsed.statistics || parsed.win_rate !== undefined) {
                        statisticsProgress = progress;
                        break;
                    }
                }
                catch (error) {
                    console.warn('[AnalyzeGrantCard] Failed to parse statistics data:', error);
                }
            }
        }
        if (!(statisticsProgress === null || statisticsProgress === void 0 ? void 0 : statisticsProgress.content))
            return null;
        try {
            return JSON.parse(statisticsProgress.content);
        }
        catch (error) {
            console.warn('[AnalyzeGrantCard] Failed to decode statistics JSON:', error);
            return null;
        }
    }, [tool]);
    const analysisResult = useMemo(() => {
        var _a;
        if (!((_a = tool.result) === null || _a === void 0 ? void 0 : _a.content))
            return null;
        try {
            return JSON.parse(tool.result.content);
        }
        catch (error) {
            console.warn('[AnalyzeGrantCard] Failed to parse analysis result:', error);
            return null;
        }
    }, [tool]);
    if (!grantInfo)
        return null;
    const grantUrl = `${paths.grants}/${grantInfo.grant_id}`;
    const grant = grantInfo.grant || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.grant) || {};
    const isFr = (_a = i18n.language) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith('fr');
    const countLocalizedOk = isFr
        ? t('program_candidates_data|accepted_applications_count_prefix', 'Based on') !== 'Based on' &&
            t('program_candidates_data|accepted_applications_count_suffix', 'accepted applications') !== 'accepted applications'
        : true;
    const displayTitle = (isFr ? grant === null || grant === void 0 ? void 0 : grant.grant_title_fr : grant === null || grant === void 0 ? void 0 : grant.grant_title_en) ||
        (isFr ? grant === null || grant === void 0 ? void 0 : grant.grant_display_title_fr : grant === null || grant === void 0 ? void 0 : grant.grant_display_title_en) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_title_en) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_display_title_en) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_title_fr) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_display_title_fr) ||
        (grant === null || grant === void 0 ? void 0 : grant.title) ||
        (grant === null || grant === void 0 ? void 0 : grant.name) ||
        (isFr ? grantInfo === null || grantInfo === void 0 ? void 0 : grantInfo.grant_title_fr : grantInfo === null || grantInfo === void 0 ? void 0 : grantInfo.grant_title_en) ||
        (isFr
            ? grantInfo === null || grantInfo === void 0 ? void 0 : grantInfo.grant_display_title_fr
            : grantInfo === null || grantInfo === void 0 ? void 0 : grantInfo.grant_display_title_en) ||
        grantInfo.display_title ||
        grantInfo.title ||
        grantInfo.grant_id;
    const description = (isFr
        ? grant === null || grant === void 0 ? void 0 : grant.grant_description_short_fr
        : grant === null || grant === void 0 ? void 0 : grant.grant_description_short_en) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_description_short_en) ||
        (grant === null || grant === void 0 ? void 0 : grant.grant_description_short_fr) ||
        '';
    grant.grant_display_title = displayTitle;
    grant.grant_description_short = description;
    const handleClickCta = () => {
        navigate(grantUrl);
    };
    return (_jsxs(ToolAnalyzeContainer, { children: [_jsx(GrantCard, Object.assign({}, grant, { type: "assistant", cardSize: "large", handleClickCta: handleClickCta, ctaText: "", withCtaArrow: true, buttonVariant: "navigation" })), ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics)) && (_jsxs(Div, { style: {
                    marginTop: theme.spacing.sm,
                    backgroundColor: theme.colors.white_1,
                    padding: theme.spacing.sm,
                    borderColor: theme.colors.grey_2,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderRadius: theme.border_radius.sm,
                }, children: [_jsx(Typography, { elementTheme: "h5", color: theme.colors.black, children: t('grant_single_labels|funding_intelligence') }), ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.win_rate) !== undefined ||
                        (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.win_rate) !== undefined) && (_jsxs(Div, { flex: "row", align: "center", gap: theme.spacing.sm, style: {
                            marginTop: theme.spacing.sm,
                        }, children: [_jsxs(Typography, { elementTheme: "body2", style: {
                                    fontWeight: theme.fontWeights.medium,
                                    marginTop: `${theme.spacing.sm}px`,
                                }, color: theme.colors.grey_3, children: [t('assistant|tool_analyze_grant_application_success_rate', 'Application success rate'), ":"] }), _jsx(Typography, { elementTheme: "body2", style: {
                                    color: theme.colors.primary,
                                    fontWeight: theme.fontWeights.semibold,
                                    marginTop: theme.spacing.sm,
                                }, color: theme.colors.primary, children: (_b = statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.win_rate_percentage) !== null && _b !== void 0 ? _b : `${(((_c = analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.win_rate) !== null && _c !== void 0 ? _c : 0) * 100).toFixed(1)}%` })] })), _jsx(Div, { flex: "row", overflow: "auto", style: {
                            marginTop: theme.spacing.sm,
                        }, children: _jsx(FavoriteCandidatesContent, { industry: (_d = ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics))) === null || _d === void 0 ? void 0 : _d.industry, location: (_e = ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics))) === null || _e === void 0 ? void 0 : _e.location, revenue: (_f = ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics))) === null || _f === void 0 ? void 0 : _f.revenue, size: (_g = ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics))) === null || _g === void 0 ? void 0 : _g.size, count: countLocalizedOk
                                ? (_h = ((statisticsData === null || statisticsData === void 0 ? void 0 : statisticsData.statistics) || (analysisResult === null || analysisResult === void 0 ? void 0 : analysisResult.statistics))) === null || _h === void 0 ? void 0 : _h.total
                                : undefined, isAccessible: accessLevel.accessLevel !== 'free' }) })] }))] }));
};
export default AnalyzeGrantCard;
